import { Trade } from "@models/trade";
import { Model } from "@models/model";
import { Validateable } from "@models/interfaces/validateable";
import { ErrorClass, ValidationError } from "bp-common-models-lib";

import { WithTime } from "@models/interfaces/with_time";
import { InfoService } from "@core-services/info.service";
import { Dictionary, SearchCallback } from "./types";
import { BestFeeCollection } from "@models/best_fee_collection";
import moment from "moment";
import { debug } from "./debug";
import { IndividualConfig } from "ngx-toastr";
import { containsString, getLocaleArray, truncate } from "@helpers/utils";
import { FiatTransaction, TradableTransaction, Transaction } from "@models/transaction";
import { EMPTY, Observable } from "rxjs";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";

export function stringToBoolean(value: string): boolean {
	return value === "true";
}

// TODO: eslint fix
/*eslint-disable @typescript-eslint/no-explicit-any*/
export function parseList(list: any[], className: any, raw: boolean = false, lang: string = ""): any[] {
	try {
		const temp = [];
		list.forEach((item) => {
			const current = new className();
			setIdAndEvent(current, item);

			setFields(current, item.attributes, lang);
			if (raw) {
				current["raw"] = item.attributes;
			}
			temp.push(current);
		});
		return temp;
	} catch (e) {
		console.error(e, "FAILED parsing list, class, raw", list, className, raw);
		return [new className()];
	}
}

// Replaces special characters in trade term downloads
// Removes duplicate whitespaces -> trims possible whitespaces from string start and end -> replaces whitespaces with hyphens
export function convertText(text: string): string {
	return text
		.replace(/[^a-zA-Z0-9]/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/\s/g, "-")
		.toLowerCase();
}

export function parseObject(object: any, className: any, raw: boolean = false, lang: string = ""): any {
	try {
		const temp = new className();

		setIdAndEvent(temp, object);

		setFields(temp, object.attributes, lang);
		if (raw) {
			temp["raw"] = object.attributes;
		}

		return temp;
	} catch (e) {
		console.error(e, "FAILED parsing object, class, raw", object, className, raw);
		return {} as any;
	}
}

export function convertArrayToDictionary<T>(array: T[], key: string): Dictionary<T> {
	return array.reduce((obj: Dictionary<T>, item: T) => ((obj[item[key]] = item), obj), {});
}

// adds all key/value pairs from sourceDict to destDict,
// but doesn't combine values if key already exists in destDict
export function combineDictionaries<T>(sourceDict: Dictionary<T>, destDict: Dictionary<T>): Dictionary<T> {
	const newDict = {};

	if (!destDict) {
		return sourceDict;
	}

	Object.keys(sourceDict).forEach((key) => {
		// if key exists in dest, use that value
		if (key in destDict) {
			newDict[key] = destDict[key];
		} else {
			newDict[key] = sourceDict[key];
		}
	});

	return newDict;
}

export function filterDictionary<T, K extends T>(
	inputDict: Dictionary<T>,
	filterFunction: (val: T, key: string) => boolean
): Dictionary<K> {
	return Object.fromEntries(
		Object.entries(inputDict).filter(([key, val]) => filterFunction(val, key))
	) as Dictionary<K>;
}

export function mapDictionary<T, K>(
	inputDict: Dictionary<T>,
	mapFunction: (original: T, key: string) => K
): Dictionary<K> {
	const outDict: Dictionary<K> = {};
	for (let k of Object.keys(inputDict)) {
		const thisVal = inputDict[k];
		outDict[k] = mapFunction(thisVal, k);
	}
	return outDict;
}

export function reduceDictionary<T, K>(
	inputDict: Dictionary<T>,
	reducerFunction: (currentVal: K, dictItem: T, key: string) => K,
	initialValue: K
): K {
	let value = initialValue;
	for (let k of Object.keys(inputDict)) {
		const thisVal = inputDict[k];
		value = reducerFunction(value, thisVal, k);
	}
	return value;
}

export function pickManyFromDictByIds<T extends { id: string }>(
	dict: Record<string, T>,
	ids: string[]
): Record<string, T> {
	const result: Record<string, T> = {};
	for (const id of ids) {
		if (dict.hasOwnProperty(id)) {
			result[id] = dict[id];
		}
	}
	return result;
}

export function setFields(current: any, attributes: any, lang: string = Helper.language): void {
	try {
		for (const attrProp in attributes) {
			if (!attributes.hasOwnProperty(attrProp)) {
				continue;
			}

			let noSuffixProp = attrProp;

			// Needs to actually end with _eng, _deu, _fra,...
			if (attrProp.endsWith("_" + lang) && lang !== "") {
				// Remove language suffix
				const sliceIndex = attrProp.lastIndexOf(lang) - 1; // Added -1 because we have char "_" (eg: _deu)
				noSuffixProp = attrProp.slice(0, sliceIndex); // Description_deu -> description
				current[attrProp] = attributes[attrProp];
			}
			if (attributes[attrProp] !== undefined) {
				if (typeof current[noSuffixProp] === "object") {
					// Is array
					if (attributes[attrProp] instanceof Array) {
						// More than 1 val
						if (attributes[attrProp].length === 0) {
							continue;
						}
						// Check recursion
						if (current[noSuffixProp][0] === undefined) {
							current[noSuffixProp] = attributes[attrProp];
						} else {
							const c = current[noSuffixProp][0].constructor;
							current[noSuffixProp] = parseList(attributes[attrProp], c, false, lang);
						}
					} else {
						if (noSuffixProp === "best_fee_collection" && current instanceof TradableTransaction) {
							current["best_fee_collection"] = parseObject(
								attributes[attrProp],
								BestFeeCollection,
								false,
								lang
							);
						}
						if (noSuffixProp === "card_fiat_wallet_transaction") {
							current["card_fiat_wallet_transaction"] = parseObject(
								attributes[attrProp],
								FiatTransaction,
								false,
								lang
							);
						}

						const needsSpecialParse: boolean =
							noSuffixProp === "related_swap_trade" || noSuffixProp === "best_fee_collection";
						if (current instanceof Trade && needsSpecialParse) {
							if (noSuffixProp === "related_swap_trade" && current.getIsSwap()) {
								current["related_swap_trade"] = parseObject(attributes[attrProp], Trade, false, lang);
							} else if (noSuffixProp === "best_fee_collection" && current.getBfcUsed()) {
								current["best_fee_collection"] = parseObject(
									attributes[attrProp],
									BestFeeCollection,
									false,
									lang
								);
							}
						} else {
							const c = current[noSuffixProp].constructor;

							// Special object
							if (attributes[attrProp]?.hasOwnProperty("attributes")) {
								current[noSuffixProp] = parseObject(attributes[attrProp], c, false, lang);
							} else {
								setFields(current[noSuffixProp], attributes[attrProp], lang);
							}
						}
					}
				} else {
					current[noSuffixProp] = attributes[attrProp];
				}
			}
		}
	} catch (e) {
		console.error(e, "FAILED parsing current, attributes", current, attributes);
	}
}

export function parseCollection<T extends Model>(msg: any, type, collection: string, rethrow = false): T[] {
	try {
		const rawCollection = msg["attributes"][collection];

		if (!rawCollection) {
			return [];
		}

		return parseList(rawCollection, type);
	} catch (e) {
		if (!rethrow) {
			console.error("[PARSING] collection: ", e);
			return [];
		}
		return null;
	}
}

function setIdAndEvent(current: any, item: any): void {
	if (current.hasOwnProperty("id") && item.hasOwnProperty("id")) {
		current["id"] = item.id;
	}
	if (item.hasOwnProperty("event") && item.hasOwnProperty("attributes")) {
		item.attributes["event"] = item["event"];
	}
}

export function getTransactionMapKeys(map: Map<string, (Transaction | Trade)[]>): string[] {
	return Array.from(map.keys());
}

export function parseApiResponseObject<T>(object: BasicApiObject): T {
	const desiredObject: T = {
		...(object.type && { _type: object.type }),
		...(object.id && { id: object.id }),
		...(object.attributes && { ...object.attributes }),
		...(object.id && !object.attributes && { ...object })
	} as unknown as T;

	Object.keys(desiredObject).forEach((key: string) => {
		let value = desiredObject[key];
		if (typeof value === "object") {
			// TODO: Handle null.
			if (value instanceof Array) {
				value = parseApiResponseArray<T>(value);
				desiredObject[key] = value;
			} else if (value instanceof Object) {
				value = value.attributes ? parseApiResponseObject<T>(value as BasicApiObject) : value;
				desiredObject[key] = value;
			}
		}
	});

	return desiredObject;
}

// TODO Change name.
export function parseApiResponseArray<T>(array: BasicApiObject[]): T[] {
	return array.map((item: BasicApiObject) => {
		if (typeof item === "object") {
			return parseApiResponseObject<T>(item);
		}
		return item;
	});
}

/** @deprecated use parseApiResponseObject in favor of this method.
 * parseApiResponseObject method now supports Objects without id or attributes.
 * Note: parseApiResponseObject also accepts type of passed object if exists,
 * saves it to _type prop that can be used later to map object to specific adapter*/

// TODO merge parseApiResponseObject with parseApiObject - check if "object" has "id" and/or "attributes" properties
// Similar as the other function, but:
// - supports nested objects without "attributes" property (e.g. portfolio performance candles)
// - does not need specific type of object passed in (e.g. BasicApiObject with "id" property)
export function parseApiObject<T>(object: any): T {
	let desiredObj = null;

	// Some (nested) objects don't have "attributes" property
	if (object.attributes) {
		desiredObj = {
			...object.attributes
		} as T;
	} else {
		desiredObj = {
			...object
		};
	}

	const raw = { ...desiredObj };

	Object.keys(raw).forEach((key: string) => {
		let value = raw[key];
		let type = typeof value;
		if (type === "object") {
			// TODO: Handle null.
			if (value instanceof Array) {
				value = parseApiArray(value);
				desiredObj[key] = value;
			} else if (value instanceof Object) {
				value = parseApiObject(value);
				desiredObj[key] = value;
			}
		}
	});

	return desiredObj;
}

export function parseApiArray(array: any[]): unknown[] {
	return array.map((item: any) => parseApiObject(item));
}

export interface BasicApiObject {
	id?: string | number;
	attributes?: { [name: string]: any };
	[key: string]: any;
}

////////////////// FIXED VERSION OF PARSER - REMOVE AFTER MAIN PARSER FUNCTION IS CHANGED //////////////////

export function parseApiResponseObject2<T>(object: BasicApiObject2): T {
	let desiredObject: T = {} as T;

	object.type ? (desiredObject = { _type: object.type } as unknown as T) : null;
	object.id ? (desiredObject = { ...desiredObject, id: object.id } as unknown as T) : null;
	object.attributes ? (desiredObject = { ...desiredObject, ...object.attributes }) : null;
	!object.id && !object.attributes ? (desiredObject = { ...object } as unknown as T) : null;

	Object.keys(desiredObject).forEach((key: string) => {
		let value = desiredObject[key];
		if (typeof value === "object") {
			// TODO: Handle null.
			if (value instanceof Array) {
				value = parseApiResponseArray2<T>(value);
				desiredObject[key] = value;
			} else if (value instanceof Object) {
				value = value.attributes ? parseApiResponseObject2<T>(value as BasicApiObject2) : value;
				desiredObject[key] = value;
			}
		}
	});

	return desiredObject;
}

// TODO Change name.
export function parseApiResponseArray2<T>(array: BasicApiObject2[]): T[] {
	return array.map((item: BasicApiObject2) =>
		typeof item !== "object" ? (item as T) : parseApiResponseObject2<T>(item)
	);
}

export interface BasicApiObject2 {
	id?: string;
	attributes?: { [name: string]: any };

	[key: string]: any;
}

////////////////// END REMOVE //////////////////

export class ModelSaver<T extends Validateable> {
	private readonly _tempModel: T = null;
	private _baseModel: T = null;

	public constructor(model: T) {
		this._tempModel = model;
	}

	public get tempModel(): T {
		return this._tempModel;
	}

	public initTempModel(model: T): void {
		if (!this.tempModel) {
			return;
		}
		this._baseModel = model;
		Object.getOwnPropertyNames(this._tempModel).forEach((key) => (this._tempModel[key] = this._baseModel[key]));
	}
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class Helper {
	public static language: string = navigator.language;
	public static infoService: InfoService = null;

	public static getCookie(key: string, tryPidPrefix: boolean = true): string | null {
		let item = null;

		// Try to take the user's specific cookie if exists (pid + cookie name)
		if (tryPidPrefix && Helper.mustNotHavePrefix(key)) {
			const pid = Helper.pid;
			if (pid !== "null") {
				const cookie = document.cookie.match(new RegExp(pid + key + "=([^;]+)"));
				if (cookie) {
					item = cookie[1];
					return "" + item;
				}
			}
		}

		// Get generic cookie without prefix:
		const foundCookie = document.cookie.match(new RegExp(`(?:^|;\\s*)${key}=([^;]+)`));
		if (foundCookie) {
			item = foundCookie[1];
		}
		return "" + item;
	}

	private static mustNotHavePrefix(key: string): boolean {
		return ["pid", "access-token", "device_id"].indexOf(key) === -1;
	}

	public static setCookie(
		key: string,
		value: string,
		addPidPrefix: boolean = false,
		crossDomain: boolean = false,
		secureFlag: boolean = true,
		sameStrict: boolean = true,
		expiryDate?: Date,
		baseSubdomain?: boolean,
		ignoreExpiry?: boolean
	): void {
		let prefix: string = ""; // None default

		// Specific user data will be saved with prefix (pid + cookie name)
		if (addPidPrefix && this.pid !== "null") {
			prefix = this.pid;
		}

		let expires = expiryDate
			? expiryDate.toUTCString()
			: new Date(+new Date() + 365 * 24 * 60 * 60 * 2000).toUTCString();

		if (ignoreExpiry) {
			expires = undefined;
		}

		let cookie = prefix + key + "=" + value + "; expires=" + expires + "; path=/";

		if (crossDomain) {
			cookie += ";domain=" + this.getDomain();
		}

		if (baseSubdomain) {
			cookie += ";domain=" + this.getBaseDomain();
		}

		if (secureFlag && location.protocol === "https:") {
			cookie += "; Secure";
		}

		if (sameStrict) {
			cookie += "; SameSite=lax";
		}

		document.cookie = cookie;
		if (debug) {
			console.log("[COOKIE]: " + cookie);
		}
	}

	public static removeCookie(key: string, crossDomain: boolean = false): void {
		let cookie = key + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/"; // Official Mozilla Docs syntax
		if (crossDomain) {
			cookie += ";domain=" + this.getDomain();
		}
		document.cookie = cookie;
	}

	public static removeLocalStorage(key): void {
		try {
			localStorage.removeItem(key);
		} catch (e) {
			console.error(e);
			// No item set
		}
	}

	// This is used after the app keys work now with Cookies and not with LocalStorage anymore.
	public static clearLocalStorage(): void {
		if (!localStorage) {
			return;
		}
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			this.removeLocalStorage(key);
		}
	}

	private static getBaseDomain(): string {
		const hostname = window.location.hostname;
		let base = hostname.split(".");
		if (base.length === 1) {
			base = [base[0]];
		} else if (hostname.includes(".preview") || hostname.includes(".staging")) {
			base = base.slice(2, base.length);
		} else {
			base = base.slice(1, base.length);
		}

		return base.reduce((prev, curr) => prev + "." + curr, "");
	}

	private static getDomain(): string {
		let base = window.location.hostname.split(".");
		if (base.length === 1) {
			base = [base[0]];
		} else {
			base = base.slice(1, base.length);
		}
		return base.reduce((prev, curr) => prev + "." + curr, "");
	}

	public static getMap<T extends WithTime>(data: T[], format: string = "YYYYMMDD"): Map<string, T[]> {
		const map = new Map<string, T[]>();
		if (data === undefined || data === null) {
			return map;
		}
		for (const d of data) {
			const key = moment(d.getTime().getDate()).format(format);

			if (map.has(key)) {
				map.get(key).push(d);
			} else {
				map.set(key, [d]);
			}
		}
		return map;
	}

	public static roundFull(num, precision, minPrecision = precision, maxPrecision = 8): string {
		const locales = getLocaleArray(this.language);
		return truncate(num, precision).toLocaleString(locales, {
			localeMatcher: "lookup",
			minimumIntegerDigits: 1,
			minimumFractionDigits: minPrecision,
			maximumFractionDigits: maxPrecision
		});
	}

	private static get pid(): string {
		return "" + Helper.getCookie("pid");
	}

	public static isInheritOf(type: typeof Model, parent: typeof Model): boolean {
		return type.prototype instanceof parent || type === parent;
	}

	public static setLanguage(lang: string): void {
		this.language = lang;
	}

	public static getLanguage(): string {
		return this.language;
	}

	// Search functionality:

	/*
		Filter and returns the list which contains the search term.
		@fields - each item from the @list should contain these fields. By default, "id" is the basic Model field.
		@extraSearchCallbacks - can be used for additional validation / checks which do not contain / not related to @fields
	 */
	public static filterBySearch<T extends Model>(
		list: T[],
		search: string,
		fields: string[] = ["id"],
		extraSearchCallbacks: SearchCallback<T>[] = []
	): T[] {
		if (!list) {
			return [];
		}

		if (!search) {
			return list;
		}

		return list.filter((item) => this.containsSearchTerm(item, search, fields, extraSearchCallbacks));
	}

	/*
		Search map. Returns true/false map. In some (eg: Prices list) cases we need to only map if an item is part of the searching list or not.
		We don't need to return the entire objects list (as in filterBySearch), so we'll save some memory when directly assigned to view.
		@fieldIdKey - this is the specific key name stored in the object map. Eg: key = "name" field: map['Ethereum'] = true, map['Bitcoin'] = false.
	 */
	public static mapBySearch<T extends Model>(
		list: T[],
		search: string,
		fields: string[] = ["id"],
		fieldIdKey: string = "id"
	): Object {
		if (!search) {
			return list;
		}

		const searchMap = {};

		list.forEach((item) => {
			const containsValue: boolean = this.containsSearchTerm(item, search, fields);
			const keyValue = item[fieldIdKey];

			searchMap[keyValue] = containsValue;
		});

		return searchMap;
	}

	/*
		Common function used by search map / filter
	 */
	public static containsSearchTerm<T extends Model>(
		model: T,
		search: string,
		fields: string[] = null,
		extraSearchCallbacks: SearchCallback<T>[] = []
	): boolean {
		if (!model) {
			return false;
		}

		let containsValue: boolean = fields.some((fieldName) => containsString(model[fieldName], search));
		containsValue =
			containsValue ||
			extraSearchCallbacks.some((callback: SearchCallback<T>) => containsString(callback(model), search));

		return containsValue;
	}

	public static handleError(e: any, data: Validateable = null, options: ErrorHandler = {}): void {
		if (!this.infoService) {
			return;
		}
		if (!options.type) {
			options.type = ValidationError;
		}

		if (e instanceof ErrorClass) {
			if (data) {
				// Data is set
				if (options.forceToast) {
					Helper.infoService.setError(e);
				}
				// We check for validation error
				if (e instanceof options.type) {
					data.setValidationErrors(e);
				} else if (options.callback) {
					// Maybe we have a fallback action set
					options.callback();
				}
			} else {
				const toastrConfig = options.toastrConfig ? options.toastrConfig : { ignore: [] };
				Helper.infoService.setError(e, toastrConfig);
			}
		} else {
			console.error(e);
		}
	}

	public static handleError$(e: any, data: Validateable = null, options: ErrorHandler = {}): Observable<void> {
		Helper.handleError(e, data, options);
		return EMPTY;
	}

	/**
	 * calculateTimeDifferenceInMiliSeconds
	 * @param unixTime: number
	 * @param setInSecond :boolean
	 * @returns number
	 */
	public static calculateTimeDifferenceInMiliSeconds(unixTime: number, setInSecond = false): number {
		if (!unixTime) {
			return 0;
		}

		const now = new Date(); // Get the current date and time
		const pastTime = new Date(unixTime); // Get the past time
		const differenceInMilliseconds = now.valueOf() - pastTime.valueOf(); // Calculate the difference in milliseconds
		const differenceInSeconds = Math.floor(differenceInMilliseconds / 1000); // Convert the difference to seconds
		const convertToMiliseconds = Math.abs(differenceInSeconds) * 1000; // Convert the difference to milliseconds

		if (setInSecond) {
			return convertToMiliseconds / 1000;
		}
		return convertToMiliseconds;
	}
}

export interface ErrorHandler {
	type?: typeof ErrorClass;
	callback?: () => void;
	forceToast?: boolean;
	toastrConfig?: Partial<IndividualConfig> & { ignore: string[] };
}

export interface StakeableHelper {
	isStakeable: boolean;
	stakeable: Stakeable;
}

export function getStakeableHelper(stakeables: Stakeable[], assetId: string): StakeableHelper {
	const stakeable = stakeables.find((stakeables) => stakeables.id === assetId);
	return {
		stakeable: stakeable,
		isStakeable: !!stakeable
	};
}

export function resetTimer(expireTime: number): Date {
	return new Date(Date.now() + expireTime);
}
